import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';

interface CampaignPerformanceRow {
  campaignId: string;
  name: string;
  leadsGenerated: number;
  leadsQualified: number;
  leadsConverted: number;
  conversionRate: number;
  pipelineValue: number;
  wonValue: number;
}

interface LeadSourceRow {
  source: string;
  totalLeads: number;
  qualifiedCount: number;
  convertedCount: number;
  conversionRate: number;
}

type SortDir = 'asc' | 'desc';

function formatCurrency(value: number): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `$${value.toFixed(0)}`;
  }
}

function SortHeader({
  label,
  field,
  activeField,
  dir,
  onSort,
  align,
}: {
  label: string;
  field: string;
  activeField: string;
  dir: SortDir;
  onSort: (field: string) => void;
  align?: 'left' | 'right';
}) {
  const isActive = field === activeField;
  return (
    <th
      className={`cursor-pointer select-none px-4 py-3 ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && <span className="text-[10px]">{dir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
      </span>
    </th>
  );
}

function sortRows<T>(rows: T[], field: string, dir: SortDir): T[] {
  return [...rows].sort((a, b) => {
    const av = (a as Record<string, unknown>)[field];
    const bv = (b as Record<string, unknown>)[field];
    if (typeof av === 'number' && typeof bv === 'number') {
      return dir === 'asc' ? av - bv : bv - av;
    }
    const as = String(av ?? '');
    const bs = String(bv ?? '');
    return dir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
  });
}

export function CampaignAnalyticsPage() {
  const [performance, setPerformance] = useState<CampaignPerformanceRow[]>([]);
  const [sources, setSources] = useState<LeadSourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [perfSort, setPerfSort] = useState<{ field: string; dir: SortDir }>({
    field: 'name',
    dir: 'asc',
  });
  const [srcSort, setSrcSort] = useState<{ field: string; dir: SortDir }>({
    field: 'source',
    dir: 'asc',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [perfRes, srcRes] = await Promise.all([
        apiClient.get<CampaignPerformanceRow[]>('/analytics/campaign-performance'),
        apiClient.get<LeadSourceRow[]>('/analytics/lead-sources'),
      ]);
      setPerformance(perfRes.data);
      setSources(srcRes.data);
    } catch {
      setError('Failed to load campaign analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function handlePerfSort(field: string) {
    setPerfSort((prev) => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  }

  function handleSrcSort(field: string) {
    setSrcSort((prev) => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  }

  const sortedPerf = sortRows(performance, perfSort.field, perfSort.dir);
  const sortedSrc = sortRows(sources, srcSort.field, srcSort.dir);

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold">Campaign Analytics</h1>

      {error && (
        <div className="rounded-md border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : (
        <>
          {/* Campaign Performance */}
          <section className="space-y-3">
            <h2 className="text-lg font-medium">Campaign Performance</h2>
            <div className="overflow-hidden rounded-lg border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted text-xs uppercase text-muted-foreground">
                  <tr>
                    <SortHeader label="Name" field="name" activeField={perfSort.field} dir={perfSort.dir} onSort={handlePerfSort} />
                    <SortHeader label="Leads Generated" field="leadsGenerated" activeField={perfSort.field} dir={perfSort.dir} onSort={handlePerfSort} align="right" />
                    <SortHeader label="Qualified" field="leadsQualified" activeField={perfSort.field} dir={perfSort.dir} onSort={handlePerfSort} align="right" />
                    <SortHeader label="Converted" field="leadsConverted" activeField={perfSort.field} dir={perfSort.dir} onSort={handlePerfSort} align="right" />
                    <SortHeader label="Conv Rate %" field="conversionRate" activeField={perfSort.field} dir={perfSort.dir} onSort={handlePerfSort} align="right" />
                    <SortHeader label="Pipeline Value" field="pipelineValue" activeField={perfSort.field} dir={perfSort.dir} onSort={handlePerfSort} align="right" />
                    <SortHeader label="Won Value" field="wonValue" activeField={perfSort.field} dir={perfSort.dir} onSort={handlePerfSort} align="right" />
                  </tr>
                </thead>
                <tbody>
                  {sortedPerf.map((row) => (
                    <tr key={row.campaignId} className="border-t">
                      <td className="px-4 py-3 font-medium">{row.name}</td>
                      <td className="px-4 py-3 text-right">{row.leadsGenerated}</td>
                      <td className="px-4 py-3 text-right">{row.leadsQualified}</td>
                      <td className="px-4 py-3 text-right">{row.leadsConverted}</td>
                      <td className="px-4 py-3 text-right">{row.conversionRate}%</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(row.pipelineValue)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(row.wonValue)}</td>
                    </tr>
                  ))}
                  {sortedPerf.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                        No campaign performance data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Lead Source Comparison */}
          <section className="space-y-3">
            <h2 className="text-lg font-medium">Lead Source Comparison</h2>
            <div className="overflow-hidden rounded-lg border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted text-xs uppercase text-muted-foreground">
                  <tr>
                    <SortHeader label="Source" field="source" activeField={srcSort.field} dir={srcSort.dir} onSort={handleSrcSort} />
                    <SortHeader label="Total Leads" field="totalLeads" activeField={srcSort.field} dir={srcSort.dir} onSort={handleSrcSort} align="right" />
                    <SortHeader label="Qualified" field="qualifiedCount" activeField={srcSort.field} dir={srcSort.dir} onSort={handleSrcSort} align="right" />
                    <SortHeader label="Converted" field="convertedCount" activeField={srcSort.field} dir={srcSort.dir} onSort={handleSrcSort} align="right" />
                    <SortHeader label="Conv Rate %" field="conversionRate" activeField={srcSort.field} dir={srcSort.dir} onSort={handleSrcSort} align="right" />
                  </tr>
                </thead>
                <tbody>
                  {sortedSrc.map((row) => (
                    <tr key={row.source} className="border-t">
                      <td className="px-4 py-3 font-medium">{row.source}</td>
                      <td className="px-4 py-3 text-right">{row.totalLeads}</td>
                      <td className="px-4 py-3 text-right">{row.qualifiedCount}</td>
                      <td className="px-4 py-3 text-right">{row.convertedCount}</td>
                      <td className="px-4 py-3 text-right">{row.conversionRate}%</td>
                    </tr>
                  ))}
                  {sortedSrc.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                        No lead source data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
